import "./application.css"
import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode, type ReactNode } from 'react'
import { hydrateRoot, createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import Layout from '@/components/Layout'
import { inertiaDefaults } from '@/inertia/defaults'
import { formatPageTitle } from '@/lib/pageTitle'
import type { FlashData } from '@/types'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn && import.meta.env.PROD) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GIT_COMMIT_SHA,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [/^\//],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

function fireGtag(name: string, params: Record<string, unknown> = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params)
  }
}

router.on('navigate', (event) => {
  const page = event.detail.page

  fireGtag('page_view', {
    page_path: page.url,
    page_location: window.location.href,
    page_title: document.title,
  })

  const flash = (page.props as { flash?: FlashData } | undefined)?.flash
  const flashEvent = flash?.gtag_event
  if (flashEvent?.event) {
    fireGtag(flashEvent.event, flashEvent.params ?? {})
  }
})

void createInertiaApp({
  title: formatPageTitle,

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<{default: ResolvedComponent}>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    page.default.layout ||= (page: ReactNode) => <Layout>{page}</Layout>

    return page
  },

  setup({ el, App, props }) {
    const app = (
      <StrictMode>
        <App {...props} />
      </StrictMode>
    )

    if (el.hasChildNodes()) {
      hydrateRoot(el, app)
    } else {
      createRoot(el).render(app)
    }
  },

  defaults: inertiaDefaults,
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
